import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, Building2, Bot } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { useMobileAIChat } from '@/context/MobileAIChatContext';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { useAuth } from '@/context/AuthContext';

interface NavItemProps {
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  onClick: () => void;
  highlight?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ icon: Icon, label, isActive, onClick, highlight }) => (
  <button
    onClick={onClick}
    className={cn(
      "flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors",
      "active:scale-95 transition-transform duration-100",
      isActive
        ? "text-primary"
        : "text-muted-foreground hover:text-foreground",
      highlight && "text-primary"
    )}
  >
    <Icon className={cn("h-5 w-5", isActive && "fill-primary/20")} />
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);

export const MobileBottomNav: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen: isAIChatOpen, openAIChat } = useMobileAIChat();
  const { businesses } = useBusinesses();
  const { user } = useAuth();

  // Don't render on desktop
  if (!isMobile) return null;

  // Don't render if user is not logged in (show on public pages only when logged in)
  if (!user) return null;

  // Determine active states
  const isCustomerActive = location.pathname.startsWith('/dashboard');
  const isEmployeeActive = location.pathname.startsWith('/employee-dashboard') ||
                          location.pathname.startsWith('/employee-portal');
  const isAgencyActive = location.pathname.startsWith('/portfolio');

  // Only show agency button if user has 2+ businesses
  const showAgencyButton = businesses && businesses.length >= 2;

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 h-16 bg-background/95 backdrop-blur-md border-t border-border z-[100]",
        "md:hidden" // Only show on mobile
      )}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-center justify-around h-full max-w-lg mx-auto px-2">
        <NavItem
          icon={Home}
          label="Customer"
          isActive={isCustomerActive}
          onClick={() => handleNavigate('/dashboard')}
        />
        <NavItem
          icon={Users}
          label="Employee"
          isActive={isEmployeeActive}
          onClick={() => handleNavigate('/employee-dashboard')}
        />
        {showAgencyButton && (
          <NavItem
            icon={Building2}
            label="Agency"
            isActive={isAgencyActive}
            onClick={() => handleNavigate('/portfolio')}
          />
        )}
        <NavItem
          icon={Bot}
          label="AI"
          isActive={isAIChatOpen}
          onClick={openAIChat}
          highlight={isAIChatOpen}
        />
      </div>
    </nav>
  );
};

export default MobileBottomNav;
